'use client';

import { captureAnalyticsClientEvent } from '@/analytics/analytics-client';
import type { PricingShowcaseRuntimeCheckout } from '@/cms/components/blocks/pricing/pricing-1/presentation';
import { PricingShowcaseRuntimeController } from '@/cms/components/blocks/pricing/pricing-1/presentation-boundary';
import { pricingCheckoutButtonClasses } from '@/cms/components/blocks/pricing/pricing-1/provider-button-classes';
import { CmsTitleGroup } from '@/cms/components/blocks/shared/cms-title-group';
import { useCurrentUser } from '@/hooks/auth/use-current-user';
import { type ReactNode, useEffect, useMemo } from 'react';
import { SiteRuntimeCheckoutProviderButtons } from '../checkout/site-runtime-checkout-provider-buttons';
import { useSiteRuntimeCheckoutProviderOptionsMap } from '../checkout/use-site-runtime-checkout-provider-options-map';
import { collectSiteRuntimeCheckoutIntentKeys } from '../pricing/checkout-intent-keys';
import { readSiteRuntimeCheckoutErrorCopy } from '../pricing/site-runtime-checkout-errors';
import type { SiteRuntimePricingBlockModel } from '../pricing/site-runtime-pricing-model';
import { buildSiteRuntimePricingShowcasePresentationModel } from '../pricing/site-runtime-pricing-showcase-presentation';
import { useSiteRuntimeActionContext } from './site-runtime-entry-action-controller';
import { useSiteRuntimePricingDialogContext } from './site-runtime-pricing-dialog-context.client';

export function SiteRuntimePricingHeader({
  description,
  eyebrow,
  title,
}: {
  description?: ReactNode;
  eyebrow?: ReactNode;
  title: string;
}) {
  const { checkoutSurface, titleOverride } =
    useSiteRuntimePricingDialogContext();
  const isPaywall = checkoutSurface === 'upgrade_dialog';

  return (
    <CmsTitleGroup
      align="center"
      as="h1"
      className={isPaywall ? '[--cms-title-group-title-max:28ch]' : undefined}
      description={isPaywall ? undefined : description}
      eyebrow={isPaywall ? undefined : eyebrow}
      size="section"
      title={titleOverride ?? title}
    />
  );
}

export function SiteRuntimePricingCards({
  header,
  locale,
  model,
}: {
  header: ReactNode;
  locale: string;
  model: SiteRuntimePricingBlockModel;
}) {
  const currentUser = useCurrentUser();
  const {
    checkoutSurface,
    conversionId,
    onCheckoutCreated,
    paywallBranchSlug,
    taskBlockPayment,
  } = useSiteRuntimePricingDialogContext();
  const { global, openLogin, reportRuntimeError } =
    useSiteRuntimeActionContext();
  const checkoutIntentKeys = useMemo(
    () => collectSiteRuntimeCheckoutIntentKeys(model),
    [model]
  );
  const checkoutProviderOptions = useSiteRuntimeCheckoutProviderOptionsMap({
    checkoutIntentKeys,
    locale,
  });
  useEffect(() => {
    captureAnalyticsClientEvent('view_item_list', {
      item_list_id: 'site_runtime_pricing',
      item_list_name: 'site_runtime_pricing',
      items: checkoutIntentKeys.map((itemId) => ({
        item_id: itemId,
        item_name: itemId,
      })),
    });
  }, [checkoutIntentKeys]);
  const showcaseModel = buildSiteRuntimePricingShowcasePresentationModel({
    locale,
    model,
  });
  const handleFreePlanClick = () => {
    if (currentUser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    openLogin({ mode: 'plain', scene: 'freeStart' });
  };
  const renderProviderCheckout = ({
    checkoutIntentKey,
    providerTone,
  }: {
    checkoutIntentKey: string;
    providerTone: 'primary' | 'secondary';
  }) => {
    const getErrorLabel = (
      errorCode: Parameters<typeof readSiteRuntimeCheckoutErrorCopy>[1]
    ) => readSiteRuntimeCheckoutErrorCopy(global.pricing, errorCode);

    return (
      <SiteRuntimeCheckoutProviderButtons
        key={checkoutIntentKey}
        checkoutIntentKey={checkoutIntentKey}
        checkoutSurface={checkoutSurface}
        classes={pricingCheckoutButtonClasses}
        conversionId={conversionId}
        dataAttributes={{
          'data-pricing-checkout-intent-key': checkoutIntentKey,
        }}
        getErrorLabel={getErrorLabel}
        locale={locale}
        onCheckoutCreated={onCheckoutCreated}
        onCheckoutError={(errorCode) => {
          reportRuntimeError(getErrorLabel(errorCode));
        }}
        onUnauthenticated={(callbackUrl) =>
          openLogin({
            callbackUrl,
            mode: 'checkout',
            scene: 'checkout',
          })
        }
        paywallBranchSlug={paywallBranchSlug}
        paypalButtonLabel={model.uiCopy.paypalButtonLabel}
        providerOptions={checkoutProviderOptions.getProviderOptions(
          checkoutIntentKey
        )}
        providerOptionsError={checkoutProviderOptions.providerOptionsError}
        providerOptionsPending={checkoutProviderOptions.providerOptionsPending}
        redirectingLabel={model.uiCopy.redirectingLabel}
        stripeButtonLabel={model.uiCopy.stripeButtonLabel}
        taskBlockPayment={taskBlockPayment}
        providerTone={providerTone}
        unavailableLabel={getErrorLabel('provider_unavailable')}
      />
    );
  };
  const checkout: PricingShowcaseRuntimeCheckout = {
    onFreePlanClick: handleFreePlanClick,
    renderSubscriptionCheckout: ({ activeSubscription, billingMode }) => {
      const plan = model.plans.find(
        (candidate) => candidate.planKey === activeSubscription?.id
      );
      const option = plan?.billingOptions.find(
        (candidate) =>
          candidate.billingCycle ===
          (billingMode === 'monthly' ? 'month' : 'year')
      );
      if (!option || !plan) {
        return null;
      }

      return renderProviderCheckout({
        checkoutIntentKey: option.checkoutIntentKey,
        providerTone: 'primary',
      });
    },
    renderCreditPackCheckout: ({ activeCreditPack }) => {
      const creditPackage = model.creditPackages.find(
        (candidate) => candidate.packageKey === activeCreditPack?.id
      );
      if (!creditPackage) {
        return null;
      }

      return renderProviderCheckout({
        checkoutIntentKey: creditPackage.checkoutIntentKey,
        providerTone: 'secondary',
      });
    },
  };

  return (
    <PricingShowcaseRuntimeController
      checkout={checkout}
      header={header}
      model={showcaseModel}
    />
  );
}
